// Shared helpers for the dashboard "indicator" widget: optional display-mode
// transforms (pure number representation, e.g. showing a 0.18 ratio as 18%)
// and period-label formatting (e.g. "Q1" instead of the raw "03" month for
// quarterly data). Both are opt-in — by default the indicator shows the raw
// value and raw period label exactly as they come from the data.
//
// Display mode only ever rescales the single selected value — it never
// compares it against another period's value, and it never appends a
// suffix on its own (use "Unit next to value" for that, e.g. typing "%"
// there once the value is already scaled to a percentage).

export const DISPLAY_MODE_OPTIONS = [
    { value: 'raw', label: 'Raw value (as returned)' },
    { value: 'percent', label: 'As percentage (× 100, e.g. 0.18 → 18)' },
    { value: 'basis_points', label: 'As basis points (× 10,000)' },
    { value: 'invert', label: 'Invert sign (× -1, e.g. for deficit/surplus)' },
    { value: 'mult1000', label: 'Multiply by 1,000' },
    { value: 'div100', label: 'Divide by 100' },
    { value: 'div1000', label: 'Divide by 1,000' },
]

export const PERIOD_FORMAT_OPTIONS = [
    { value: 'raw', label: 'Raw (e.g. 2020/03)' },
    { value: 'quarter', label: 'Quarter (e.g. Q1 2020)' },
]

const MONTH_TO_QUARTER = {
    '01': 1, '02': 1, '03': 1,
    '04': 2, '05': 2, '06': 2,
    '07': 3, '08': 3, '09': 3,
    '10': 4, '11': 4, '12': 4,
}

// `dateLabel` is expected as "YYYY-MM" (the shape used by timeframe options
// throughout the indicator widget/editor).
export function formatQuarterLabel(dateLabel) {
    const [year, month] = String(dateLabel || '').split('-')
    const quarter = MONTH_TO_QUARTER[month]
    if (!year || !quarter) return dateLabel
    return `Q${quarter} ${year}`
}

export function formatPeriodLabel(dateLabel, frequency, periodFormat) {
    if (!dateLabel) return ''
    if (periodFormat === 'quarter' && frequency === 'Quarterly') {
        return formatQuarterLabel(dateLabel)
    }
    if (frequency === 'Annual') return String(dateLabel).split('-')[0]
    const [year, month] = String(dateLabel).split('-')
    return month ? `${year}/${month}` : year
}

// Rescales the single raw value according to `displayMode` — never compares
// it against another period, and never adds its own suffix (that's what
// "Unit next to value" is for). Returns { value, suffix: '' } ready to
// render; `suffix` is kept in the return shape for callers, always empty.
export function computeDisplayValue(rawValue, displayMode, round) {
    const numericRaw = Number(rawValue)
    if (!Number.isFinite(numericRaw)) return { value: rawValue, suffix: '' }

    switch (displayMode) {
        case 'percent':
            return { value: round(numericRaw * 100, 2), suffix: '' }
        case 'basis_points':
            return { value: round(numericRaw * 10000, 2), suffix: '' }
        case 'invert':
            return { value: round(numericRaw * -1, 2), suffix: '' }
        case 'mult1000':
            return { value: round(numericRaw * 1000, 2), suffix: '' }
        case 'div100':
            return { value: round(numericRaw / 100, 4), suffix: '' }
        case 'div1000':
            return { value: round(numericRaw / 1000, 4), suffix: '' }
        case 'raw':
        default:
            return { value: round(numericRaw, 2), suffix: '' }
    }
}
